import React, { Dispatch, SetStateAction } from 'react';
import {
    ProviderOutlined,
    PackOutlined,
    List,
    CloseOutlined,
    NetworkOutlined,
    AddToCollectionOutlined,
} from '@landr/maestro';
import { RouterLink } from 'components/CommonUi/RouterLink';
import { t, Trans } from '@lingui/macro';
import { analytics } from 'utils/analytics';
import { usePrivateActionClickHandler } from 'hooks/usePrivateActionClickHandler/usePrivateActionClickHandler';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import useDeleteCollectionSampleButton from 'components/DeleteCollectionSampleButton/useDeleteCollectionSampleButton';
import useSelectorButton from 'components/CommonUi/SelectorButton/useSelectorButton';
import { InstrumentationOptions } from 'types/instrumentationOptions';
import addQueryStringTo from 'helpers/addQueryStringTo';
import { Sample } from 'types/generated/graphql';
import { RoutePaths, SamplesTableActions } from 'types/enums';
import { useOpenAddToCollectionPrompt } from 'actions/UserCollection';
import useAddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton/useAddSampleToCollectionButton';
import useFavoriteButton from 'components/FavoriteButton/useFavoriteButton';
import { useSamplesTableActionsList } from './useSamplesTableActionsList';
export interface SamplesTableActionsListProps {
    biggerThanMobile: boolean;
    menuPackSlug: string;
    sample: Sample;
    hide: () => void;
    actionsToHide?: {
        [key in SamplesTableActions]: boolean;
    };
    setPopover: Dispatch<SetStateAction<React.ReactElement | undefined>>;
    page: number;
    instrumentationOptions: InstrumentationOptions;
}

export const SamplesTableActionsList: React.FC<
    SamplesTableActionsListProps
> = ({
    biggerThanMobile,
    menuPackSlug,
    sample,
    hide,
    actionsToHide,
    setPopover,
    instrumentationOptions,
}) => {
    const {
        isPackDetailsPage,
        isCollectionDetailsPage,
        isPartnerDetails,
        partner,
    } = useSamplesTableActionsList({ sample, biggerThanMobile });

    const { handleOnClick: handleSelectorOnClick } = useSelectorButton({
        sample,
        instrumentationOptions,
    });

    const { handleClick: handleDeleteCollectionSample } =
        useDeleteCollectionSampleButton({ samples: sample ? [sample] : [] });

    const handleDeleteCollectionSampleClick = usePrivateActionClickHandler({
        authOnly: true,
        onClick: handleDeleteCollectionSample,
        userAction: {
            type: '',
            description: t`delete collection sample`,
            args: [],
        },
    });

    const { anchorRef, openAddToCollection, CollectionsList } =
        useOpenAddToCollectionPrompt();

    const setCollectionListToPopover = () => {
        setPopover &&
            setPopover(<CollectionsList hide={hide} samples={[sample]} />);
    };

    const { handleOnClick: handleAddToCollectionClick } =
        useAddSampleToCollectionButton({
            samples: [sample],
            setCollectionListToPopover,
            source: { interaction: 'Samples Table' },
            hide,
            openAddToCollection,
        });

    const { handleClick: handleFavoriteClick, icon: favoriteIcon } =
        useFavoriteButton({
            item: sample,
        });

    return (
        <List data-testid="samples-table-actions-list">
            {!biggerThanMobile && (
                <>
                    <List.Item
                        as="button"
                        icon={favoriteIcon}
                        onClick={handleFavoriteClick}
                    >
                        <Trans>Add to favorites</Trans>
                    </List.Item>
                    <List.Item
                        as="button"
                        icon={AddToCollectionOutlined}
                        ref={anchorRef}
                        onClick={handleAddToCollectionClick}
                    >
                        <Trans>Add to my collection</Trans>
                    </List.Item>
                    <List.Divider />
                </>
            )}
            {!actionsToHide?.selector && (
                <List.Item
                    as={RouterLink}
                    to={addQueryStringTo(
                        `/${RoutePaths.Recommendations}/${sample.guid}`,
                    )}
                    icon={NetworkOutlined}
                    onClick={handleSelectorOnClick}
                >
                    <Trans>Find similar sounds</Trans>
                </List.Item>
            )}
            {biggerThanMobile && <List.Divider />}
            {isCollectionDetailsPage && sample && (
                <List.Item
                    as="button"
                    onClick={handleDeleteCollectionSampleClick}
                    icon={CloseOutlined}
                    aria-label={t`Remove from collection`}
                >
                    <Trans>Remove from collection</Trans>
                </List.Item>
            )}
            {!isPackDetailsPage && (
                <List.Item
                    as={RouterLink}
                    to={addQueryStringTo(
                        `/${RoutePaths.Packs}/${menuPackSlug}`,
                    )}
                    icon={PackOutlined}
                    onClick={(): void => {
                        hide();
                        analytics.trackCTAClicked({
                            ctaGoal: CtaGoal.ViewDetail,
                            ctaType: CtaType.Dropdown,
                            interactionSource:
                                InteractionSource.AssetActionDropdown,
                            ctaDestination: `/${RoutePaths.Packs}/${menuPackSlug}`,
                            ctaText: 'View pack',
                        });
                    }}
                >
                    <Trans>View pack</Trans>
                </List.Item>
            )}
            {partner && !isPartnerDetails && (
                <List.Item
                    as={RouterLink}
                    to={`/${partner.route}/${partner.slug}`}
                    icon={ProviderOutlined}
                    onClick={(): void => {
                        analytics.trackCTAClicked({
                            ctaGoal: CtaGoal.ViewDetail,
                            ctaType: CtaType.Dropdown,
                            interactionSource:
                                InteractionSource.AssetActionDropdown,
                            ctaDestination: `/${partner.route}/${partner.slug}`,
                            ctaText: 'View partner',
                        });
                    }}
                >
                    <Trans>View partner</Trans>
                </List.Item>
            )}
        </List>
    );
};
